export const COOKIE_MIRROR_PREFIX = "cc_cookie_mirror__";
export const UID_SHADOW_HEADER = "x-cc-uid-shadow";
export const MATOMO_CONSENT_SHADOW_HEADER = "x-cc-matomo-consent-shadow";
export const QUESTION_START_SHADOW_HEADER = "x-cc-question-start";
export const QUESTION_START_ELAPSED_HEADER = "x-cc-question-elapsed-ms";

export const COOKIE_NAME_UID = process.env.NEXT_PUBLIC_COOKIE_NAME || "cc_uid";
export const COOKIE_NAME_MATOMO_CONSENT = "cc_matomo_consent";
export const COOKIE_NAME_PWA_BONUS_STATE = "cc_pwa_bonus_state";
export const COOKIE_NAME_PWA_INSTALL_PATH = "pwa_install_path";
export const COOKIE_NAME_PWA_LAST_PATH = "pwa_last_path";
export const COOKIE_NAME_QUESTION_START = "cc_question_start";

type ConsentState = "unknown" | "accepted" | "rejected";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const LEGACY_MIGRATION_COOKIE_NAMES = new Set<string>([
    COOKIE_NAME_UID,
    COOKIE_NAME_MATOMO_CONSENT,
    COOKIE_NAME_PWA_BONUS_STATE,
    COOKIE_NAME_PWA_INSTALL_PATH,
    COOKIE_NAME_PWA_LAST_PATH,
]);

function mirrorKey(cookieName: string): string {
    return `${COOKIE_MIRROR_PREFIX}${cookieName}`;
}

function legacyMirrorKey(cookieName: string): string {
    return cookieName;
}

function isLegacyMigrationKey(cookieName: string): boolean {
    return LEGACY_MIGRATION_COOKIE_NAMES.has(cookieName);
}

function getStorage(): Storage | null {
    if (typeof window === "undefined") return null;
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

function getCookieValue(name: string): string | null {
    if (typeof document === "undefined") return null;
    const raw = document.cookie ?? "";
    for (const part of raw.split(";")) {
        const entry = part.trim();
        if (!entry) continue;
        const idx = entry.indexOf("=");
        if (idx < 0) continue;
        const cookieName = entry.slice(0, idx).trim();
        if (cookieName !== name) continue;
        const value = entry.slice(idx + 1).trim();
        return decodeURIComponent(value);
    }
    return null;
}

function canWriteMirror(cookieName: string, value: string, storage: Storage): boolean {
    return true;
}

function fallbackUuid(): string {
    const time = Date.now().toString(16).padStart(12, "0");
    const random = Math.random().toString(16).slice(2).padEnd(20, "0");
    return `${time.slice(0, 8)}-${time.slice(8, 12)}-4${random.slice(0, 3)}-a${random.slice(3, 6)}-${random.slice(6, 18)}`;
}

function createUid(): string {
    const randomUUID = globalThis.crypto?.randomUUID;
    if (typeof randomUUID === "function") return randomUUID.call(globalThis.crypto);
    return fallbackUuid();
}

function getOrCreateUidMirror(): string {
    const existing = getMirroredCookie(COOKIE_NAME_UID);
    if (existing && UUID_REGEX.test(existing)) return existing;
    const uid = createUid();
    setMirroredCookie(COOKIE_NAME_UID, uid);
    return uid;
}

export function setMirroredCookie(cookieName: string, value: string): void {
    const storage = getStorage();
    if (!storage) return;
    if (!canWriteMirror(cookieName, value, storage)) return;
    storage.setItem(mirrorKey(cookieName), value);
}

export function getMirroredCookie(cookieName: string): string | null {
    const storage = getStorage();
    if (!storage) return null;
    const mirroredValue = storage.getItem(mirrorKey(cookieName));
    if (mirroredValue != null && mirroredValue !== "") return mirroredValue;
    const legacyValue = storage.getItem(legacyMirrorKey(cookieName));
    return legacyValue != null && legacyValue !== "" ? legacyValue : null;
}

export function removeMirroredCookie(cookieName: string): void {
    const storage = getStorage();
    if (!storage) return;
    storage.removeItem(mirrorKey(cookieName));
    storage.removeItem(legacyMirrorKey(cookieName));
}

export function pruneLegacyMirroredCookies(): void {
    const storage = getStorage();
    if (!storage) return;

    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
        (key): key is string => Boolean(key)
    );

    const keysToRemove: string[] = [];
    for (const key of keys) {
        if (key.startsWith(COOKIE_MIRROR_PREFIX)) continue;
        if (!isLegacyMigrationKey(key) && !key.startsWith("location_id__")) continue;

        const legacyValue = storage.getItem(key);
        if (legacyValue == null || legacyValue === "") {
            keysToRemove.push(key);
            continue;
        }

        const prefixedKey = mirrorKey(key);
        if (storage.getItem(prefixedKey) == null) {
            storage.setItem(prefixedKey, legacyValue);
        }
        keysToRemove.push(key);
    }

    for (const key of keysToRemove) {
        storage.removeItem(key);
    }
}

export function clearAllMirroredCookies(): void {
    const storage = getStorage();
    if (!storage) return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (!key) continue;
        if (key.startsWith(COOKIE_MIRROR_PREFIX)) keysToRemove.push(key);
    }
    for (const key of keysToRemove) {
        storage.removeItem(key);
    }
    const allKeys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
        (key): key is string => Boolean(key)
    );
    for (const key of allKeys) {
        if (isLegacyMigrationKey(key) || key.startsWith("location_id__")) {
            storage.removeItem(key);
        }
    }
}

export function setMirroredConsent(consent: Exclude<ConsentState, "unknown">): void {
    // Keep API stable for callers; technical consent is always treated as accepted.
    return;
}

export function getMirroredConsent(): ConsentState {
    return "accepted";
}

export function appendUidShadowHeader(headersInit?: HeadersInit): Headers {
    const headers = new Headers(headersInit);
    const storage = getStorage();
    const matomoConsent =
        getCookieValue(COOKIE_NAME_MATOMO_CONSENT) ??
        (storage ? getMirroredCookie(COOKIE_NAME_MATOMO_CONSENT) : null);
    if (matomoConsent === "accepted" || matomoConsent === "rejected") {
        headers.set(MATOMO_CONSENT_SHADOW_HEADER, matomoConsent);
    }
    headers.set(UID_SHADOW_HEADER, getOrCreateUidMirror());
    return headers;
}

export function mirrorLocationSelection(
    customerSlug: string,
    departmentSlug: string,
    locationId: number
): void {
    const cookieName = `location_id__${customerSlug}__${departmentSlug}`;
    setMirroredCookie(cookieName, String(locationId));
}

export function getMirroredLocationSelection(
    customerSlug: string,
    departmentSlug: string
): number | null {
    const cookieName = `location_id__${customerSlug}__${departmentSlug}`;
    const raw = getMirroredCookie(cookieName);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? Math.floor(value) : null;
}

export function mirrorPathCookie(cookieName: string, path: string): void {
    setMirroredCookie(cookieName, path);
}

export function setMirroredPwaBonusStatePending(): void {
    setMirroredCookie(COOKIE_NAME_PWA_BONUS_STATE, "1");
}

export function setMirroredPwaBonusStateClaimed(): void {
    setMirroredCookie(COOKIE_NAME_PWA_BONUS_STATE, "2");
}

export function setMirroredUid(uid: string): void {
    if (!UUID_REGEX.test(uid)) return;
    setMirroredCookie(COOKIE_NAME_UID, uid);
}

export function syncUidMirrorFromResponse(response: Response): void {
    const uid = response.headers.get(UID_SHADOW_HEADER);
    if (!uid) return;
    setMirroredUid(uid);
}

export function appendQuestionStartShadowHeader(headersInit?: HeadersInit): Headers {
    const headers = new Headers(headersInit);
    const questionStartToken = getMirroredCookie(COOKIE_NAME_QUESTION_START);
    if (questionStartToken) {
        headers.set(QUESTION_START_SHADOW_HEADER, questionStartToken);
    }
    return headers;
}

export function syncQuestionStartMirrorFromResponse(response: Response): void {
    const questionStartToken = response.headers.get(QUESTION_START_SHADOW_HEADER);
    if (!questionStartToken) return;
    setMirroredCookie(COOKIE_NAME_QUESTION_START, questionStartToken);
}

export function readQuestionElapsedMsFromResponse(response: Response): number {
    const elapsedRaw = Number(response.headers.get(QUESTION_START_ELAPSED_HEADER));
    return Number.isFinite(elapsedRaw) ? Math.max(0, Math.floor(elapsedRaw)) : 0;
}

